import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline } from "@/components/primitives";
import { StewardStoneNav } from "@/components/StewardStoneNav";

const SECTIONS = [
  { t: "Hospital & Health System Operations", d: "Consulting rooted in the realities of hospital and health-system operations — how care, staffing, and administration actually function day to day." },
  { t: "Organizational & Operational Assessment", d: "Entering an existing environment to understand how it truly operates, not how it is assumed to operate." },
  { t: "Workflow & Gap Analysis", d: "Identifying where processes break down, where risk accumulates, and where value and time are lost." },
  { t: "Performance Improvement Planning", d: "Practical strategies to strengthen operations, reduce friction, and improve measurable outcomes." },
  { t: "Leadership & Executive Advisory", d: "Working alongside leadership to translate complex operational problems into clear, executable direction." },
  { t: "Implementation Strategy", d: "Actionable improvement planning an organization can realistically execute and sustain." },
];

export default function HealthcareAdvisory() {
  return (
    <div className="glow-navy min-h-screen">
      <Seo title="Healthcare Advisory — Steward & Stone" description="Steward & Stone Healthcare Advisory — hospital and healthcare operational consulting: organizational and operational assessment, workflow analysis, performance improvement, and implementation strategy." />
      <StewardStoneNav />

      <section className="pt-12 md:pt-16 pb-8 px-6 text-center">
        <Reveal>
          <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Steward &amp; Stone</p>
          <h1 className="mt-3 font-serif2 text-4xl md:text-5xl text-[var(--bone)] leading-tight">Healthcare Advisory</h1>
          <div className="mt-6 max-w-2xl mx-auto space-y-4 text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed text-left">
            <p>Healthcare professionals should not have to compensate indefinitely for systems that make good care harder to provide.</p>
            <p>When staffing, communication, workflow, leadership decisions, and financial pressures fall out of alignment, the burden does not remain inside an administrative report. It reaches the nurse trying to manage an impossible assignment, the physician waiting for information that should already be available, and the patient experiencing the delay.</p>
            <p>Steward &amp; Stone examines the operational reality behind those moments. We look beyond what the process says should happen to understand what actually happens—and why.</p>
          </div>
        </Reveal>
      </section>

      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal><Hairline className="opacity-70 mb-12" /></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="corner-frame px-7 py-8 h-full">
                  <h3 className="font-serif2 text-xl md:text-2xl text-[var(--bone)]">{s.t}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-6 text-center">
        <Reveal className="max-w-2xl mx-auto">
          <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)] leading-loose">
            Steward &amp; Stone provides strategic and operational advisory only. We do not provide clinical, medical, legal, accounting, or licensed investment services.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-8"><PillButton to="/steward-and-stone/assessment" testid="healthcare-inquiry">Request a Review</PillButton></Reveal>
      </section>
    </div>
  );
}
